import {
  FilterState,
  Reference,
  Research,
  ResearchId,
} from "../../state/AppState.ts";
import { HttpClient, Result } from "../../../http/HttpClient.ts";
import { ApiService } from "../ApiService.interface.ts";
import {
  FilterApiService,
  FilterOptions,
} from "./FilterService/FilterApiService.ts";
import { KnowledgeSourceApiService } from "./KnowledgeSourceService/KnowledgeSourceApiService.ts";
import { MessageApiService } from "./MessageService/MessageApiService.ts";
import { ReferenceApiService } from "./ReferenceService/ReferenceApiService.ts";
import { ResearchApiService } from "./ResearchService/ResearchApiService.ts";
import { SummaryApiService } from "./SummaryService/SummaryApiService.ts";
import { TermsAndConditionsApiService } from "./TermsAndConditionsService/TermsAndConditionsApiService.ts";

export interface HttpApiServiceConfig {
  enableDynamicFilters: boolean;
  enableNewTermsAndConditionsFlow: boolean;
}

export class HttpApiService implements ApiService {
  filterApiService: FilterApiService;
  knowledgeSourceApiService: KnowledgeSourceApiService;
  researchApiService: ResearchApiService;
  messageApiService: MessageApiService;
  referenceApiService: ReferenceApiService;
  summaryApiService: SummaryApiService;
  termsAndConditionsApiService: TermsAndConditionsApiService;

  constructor(
    private readonly httpClient: HttpClient,
    config: Partial<HttpApiServiceConfig> = {},
  ) {
    const serviceConfig: HttpApiServiceConfig = {
      enableDynamicFilters: false,
      enableNewTermsAndConditionsFlow: false,
      ...config,
    };

    this.filterApiService = new FilterApiService(this.httpClient, {
      enableDynamicFilters: serviceConfig.enableDynamicFilters,
    });
    this.knowledgeSourceApiService = new KnowledgeSourceApiService(
      this.httpClient,
    );
    this.researchApiService = new ResearchApiService(this.httpClient);
    this.messageApiService = new MessageApiService(this.httpClient);
    this.referenceApiService = new ReferenceApiService(this.httpClient);
    this.summaryApiService = new SummaryApiService(this.httpClient);
    this.termsAndConditionsApiService = new TermsAndConditionsApiService(
      this.httpClient,
      {
        enableNewTermsAndConditionsFlow:
          serviceConfig.enableNewTermsAndConditionsFlow,
      },
    );
  }

  regenerateSummary(param: { researchId: ResearchId }): Result {
    return this.summaryApiService.regenerateSummary(param);
  }

  async getFilters(payload: { researchId: string }): Result<FilterState> {
    return this.filterApiService.getFilters(payload);
  }

  async setFilters(payload: {
    researchId: string;
    filters: Partial<FilterState>;
  }): Result {
    return this.filterApiService.setFilters(payload);
  }

  async getFilterOptions(payload: {
    researchId: string;
  }): Result<FilterOptions> {
    return this.filterApiService.getFilterOptions(payload);
  }

  async getKnowledgeSources(): Result<{
    knowledgeSources: { name: string; repoUrl?: string }[];
  }> {
    return this.knowledgeSourceApiService.getKnowledgeSources();
  }

  getFiltersByKnowledgeSource: ApiService["getFiltersByKnowledgeSource"] = (
    payload,
  ) => this.knowledgeSourceApiService.getFiltersByKnowledgeSource(payload);

  async createResearch(payload: {
    knowledgeSourceId: string;
    researchGoal: string;
    additionalQuestions: string;
    researchType?: string;
  }): Result<{ id: string }> {
    return this.researchApiService.createResearch(payload);
  }

  async createChatFirstResearch(payload: {
    initialMessage: string;
    knowledgeSourceId?: string;
  }): Result<{ id: string }> {
    return this.researchApiService.createChatFirstResearch(payload);
  }

  async getResearch(payload: { id: string }): Result<Research> {
    return this.researchApiService.getResearch(payload);
  }

  async renameResearch(payload: {
    researchId: ResearchId;
    name: string;
  }): Result<{ research: Research }> {
    return this.researchApiService.renameResearch(payload);
  }

  async getResearchList(): Result<{ researches: Research[] }> {
    return this.researchApiService.getResearchList();
  }

  submitMessage: ApiService["submitMessage"] = (payload) =>
    this.messageApiService.submitMessage(payload);

  getResearchMessages: ApiService["getResearchMessages"] = (payload) =>
    this.messageApiService.getResearchMessages(payload);

  getMessage: ApiService["getMessage"] = (payload) =>
    this.messageApiService.getMessage(payload);

  async getReferences(payload: {
    researchId: string;
  }): Result<{ references: Reference[] }> {
    return this.referenceApiService.getReferences(payload);
  }

  async removeReferences(payload: {
    researchId: string;
    referenceIds: string[];
  }): Result {
    return this.referenceApiService.removeReferences(payload);
  }

  async deleteResearch(payload: { researchId: string }): Result {
    return this.researchApiService.deleteResearch(payload);
  }

  async getResearchTypes(): Result<
    {
      name: string;
      description: string;
      display_name: string;
    }[]
  > {
    return this.researchApiService.getResearchTypes();
  }

  async getTermsAndConditionsStatus(payload: {
    appContext: "insights_agent" | "cla" | "pla";
  }): Result<{
    status: "pending_acceptance" | "accepted" | "acceptance_not_required";
  }> {
    return this.termsAndConditionsApiService.getTermsAndConditionsStatus(
      payload,
    );
  }

  async acceptTermsAndConditions(payload: {
    appContext: "insights_agent" | "cla" | "pla";
  }): Result<{ status: "accepted" }> {
    return this.termsAndConditionsApiService.acceptTermsAndConditions(payload);
  }

  async getLatestTermsAndConditions(payload: {
    appContext: "insights_agent" | "cla" | "pla";
  }): Result<{ content: string }> {
    return this.termsAndConditionsApiService.getLatestTermsAndConditions(
      payload,
    );
  }
}
